import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DropResult
} from "@hello-pangea/dnd";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import DeleteIconButton from "@/components/ui/delete-icon-button";
import { GripVertical, User } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { ProposalRecipient, ProposalRecipientRole } from "../types";
import { RecipientRoleSelect } from "./RecipientRoleSelect";

const ROW =
  "flex flex-1 min-w-0 items-center gap-6 p-2 rounded-2xl border border-border-widget-card bg-surface-widget-card";

interface RowProps {
  recipient: ProposalRecipient;
  signerUnavailableReason?: string;
  onRoleChange: (key: string, role: ProposalRecipientRole) => void;
  onRemove: (key: string) => void;
}

const RecipientRow: React.FC<RowProps> = ({
  recipient,
  signerUnavailableReason,
  onRoleChange,
  onRemove
}) => {
  const { t } = useTranslation();
  // A typed address is already the name, so it isn't repeated under it.
  const details = [
    recipient.name !== recipient.email ? recipient.email : undefined,
    recipient.company,
    recipient.jobTitle
  ].filter(Boolean);
  return (
    <div className={ROW} data-test={`recipient-${recipient.key}`}>
      <div className="flex flex-1 min-w-0 items-center gap-4">
        <LogoIconContainer
          index={-1}
          className="size-9"
          iconSize="5"
          icon={<User />}
        />
        <div className="flex flex-col min-w-0">
          <Text variant="body" className="truncate" hasTruncatedTooltip>
            {recipient.name}
          </Text>
          {details.length > 0 ? (
            <div className="flex items-center gap-2 min-w-0">
              {details.map((detail) => (
                <Text
                  key={detail}
                  variant="bodySecondary"
                  className="truncate min-w-0"
                  hasTruncatedTooltip
                >
                  {detail}
                </Text>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <RecipientRoleSelect
          role={recipient.role}
          signerUnavailableReason={signerUnavailableReason}
          onChange={(role) => onRoleChange(recipient.key, role)}
          aria-label={t("contracts.proposals.recipients.roleLabel", {
            name: recipient.name
          })}
          data-test={`recipient-${recipient.key}-role`}
        />
        <DeleteIconButton
          className="translate-y-0"
          onClick={() => onRemove(recipient.key)}
          aria-label={t("contracts.proposals.recipients.remove", {
            name: recipient.name
          })}
          data-test={`recipient-${recipient.key}-remove`}
        />
      </div>
    </div>
  );
};

interface ListProps {
  recipients: ProposalRecipient[];
  ordered: boolean;
  // Set while every signature place is taken.
  signerUnavailableReason?: string;
  onRoleChange: (key: string, role: ProposalRecipientRole) => void;
  onRemove: (key: string) => void;
  onMove: (from: number, to: number) => void;
}

// The dialog is centred with a transform, which offsets a dragged row's fixed
// position, so the row being dragged is drawn from the document body.
const DraggableRecipient: React.FC<
  Omit<ListProps, "recipients" | "ordered" | "onMove"> & {
    recipient: ProposalRecipient;
    index: number;
  }
> = ({ recipient, index, ...rowProps }) => {
  const { t } = useTranslation();
  const render = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) => {
    const item = (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={cn(
          "flex items-center gap-2 pb-2",
          snapshot.isDragging && "z-[60]"
        )}
      >
        <div
          {...provided.dragHandleProps}
          aria-label={t("contracts.proposals.recipients.reorder", {
            name: recipient.name
          })}
          className="flex items-center justify-center p-0.5 rounded-md hover:bg-surface-hover-soft cursor-grab"
          data-test={`recipient-${recipient.key}-drag`}
        >
          <IconContainer size="5" color="secondary">
            <GripVertical />
          </IconContainer>
        </div>
        <RecipientRow recipient={recipient} {...rowProps} />
      </div>
    );
    return snapshot.isDragging ? createPortal(item, document.body) : item;
  };
  return (
    <Draggable draggableId={recipient.key} index={index}>
      {render}
    </Draggable>
  );
};

export const RecipientList: React.FC<ListProps> = ({
  recipients,
  ordered,
  onMove,
  ...rowProps
}) => {
  if (!ordered) {
    return (
      <div className="flex flex-col gap-2" data-test="recipient-list">
        {recipients.map((recipient) => (
          <RecipientRow
            key={recipient.key}
            recipient={recipient}
            {...rowProps}
          />
        ))}
      </div>
    );
  }
  const onDragEnd = (result: DropResult) => {
    if (
      result.destination &&
      result.destination.index !== result.source.index
    ) {
      onMove(result.source.index, result.destination.index);
    }
  };
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="proposal-recipients">
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="flex flex-col"
            data-test="recipient-list"
          >
            {recipients.map((recipient, index) => (
              <DraggableRecipient
                key={recipient.key}
                recipient={recipient}
                index={index}
                {...rowProps}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
